import React, { useEffect, useState, useCallback } from 'react';
import { ordersAPI, usersAPI, shopsAPI, productsAPI, categoriesAPI } from '../services/api';
import { Order, OrderStatus, User, Shop, Product, Category, OrderItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { printInvoice } from '../utils/printInvoice';
import OrderItemsModal from '../components/OrderItemsModal';
import ProductAttrs from '../components/ProductAttrs';
import './OrdersManagement.css';

type ModalMode = 'status' | 'edit' | 'reject' | null;

const STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING: 'Ожидает',
  CONFIRMED: 'Подтверждён',
  SHIPPED: 'Отправлен',
  DELIVERED: 'Доставлен',
  CANCELLED: 'Отменён',
  REJECTED: 'Отклонён',
};

const getStatusColor = (status: OrderStatus) => {
  switch (status) {
    case 'PENDING': return '#f39c12';
    case 'CONFIRMED': return '#3498db';
    case 'SHIPPED': return '#9b59b6';
    case 'DELIVERED': return '#27ae60';
    case 'CANCELLED': return '#e74c3c';
    case 'REJECTED': return '#dc2626';
    default: return '#95a5a6';
  }
};

const OrdersManagement: React.FC = () => {
  const { isAdmin } = useAuth();

  // Lists
  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<Product[]>([]);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [agentFilter, setAgentFilter] = useState('');
  const [shopFilter, setShopFilter] = useState('');

  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [itemsModal, setItemsModal] = useState<{ orderId: number; items: OrderItem[] } | null>(null);

  // Status form
  const [newStatus, setNewStatus] = useState<OrderStatus>('PENDING');
  const [statusNotes, setStatusNotes] = useState('');

  // Reject form
  const [rejectionReason, setRejectionReason] = useState('');

  // Edit form
  const [editItems, setEditItems] = useState<Array<{ productId: number; quantity: number }>>([]);
  const [editNotes, setEditNotes] = useState('');
  const [editShopId, setEditShopId] = useState('');
  const [editAgentId, setEditAgentId] = useState('');
  const [editPaymentType, setEditPaymentType] = useState<'CASH' | 'CREDIT' | 'ADVANCE'>('CASH');
  const [editDueDate, setEditDueDate] = useState('');
  const [editCategory, setEditCategory] = useState('');

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 10 };
      if (statusFilter) params.status = statusFilter;
      if (agentFilter) params.agentId = parseInt(agentFilter);
      if (shopFilter) params.shopId = parseInt(shopFilter);
      const data = await ordersAPI.getAll(params);
      setOrders(data.data);
      setMeta(data.meta);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, agentFilter, shopFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    usersAPI.getAll().then(setUsers).catch(() => {});
    shopsAPI.getAll({ limit: 200 }).then((r) => setShops(r.data)).catch(() => {});
    categoriesAPI.getAll().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (editCategory) {
      productsAPI.getAll({ categoryId: parseInt(editCategory), limit: 200 })
        .then((r) => setCategoryProducts(r.data))
        .catch(() => {});
    } else {
      setCategoryProducts([]);
    }
  }, [editCategory]);

  // ── Status modal ───────────────────────────────────────────────────────────
  const openStatusModal = (order: Order) => {
    setActiveOrder(order);
    setNewStatus(order.status);
    setStatusNotes('');
    setModalMode('status');
  };

  const handleStatusUpdate = async () => {
    if (!activeOrder) return;
    try {
      await ordersAPI.updateStatus(activeOrder.id, { status: newStatus, notes: statusNotes || undefined });
      setModalMode(null);
      if (newStatus === 'DELIVERED') {
        // Fetch fresh order with all relations for the invoice
        const fresh = await ordersAPI.getOne(activeOrder.id);
        printInvoice(fresh);
      }
      fetchOrders();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при обновлении статуса');
    }
  };

  // ── Reject modal ───────────────────────────────────────────────────────────
  const openRejectModal = (order: Order) => {
    setActiveOrder(order);
    setRejectionReason('');
    setModalMode('reject');
  };

  const handleReject = async () => {
    if (!activeOrder) return;
    if (!rejectionReason.trim()) {
      alert('Укажите причину отказа');
      return;
    }
    try {
      await ordersAPI.updateStatus(activeOrder.id, {
        status: 'REJECTED',
        rejectionReason: rejectionReason.trim(),
      });
      setModalMode(null);
      fetchOrders();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при отклонении');
    }
  };

  // ── Edit modal ─────────────────────────────────────────────────────────────
  const openEditModal = (order: Order) => {
    setActiveOrder(order);
    setEditItems(order.items?.map((i) => ({ productId: i.productId, quantity: Number(i.quantity) })) ?? []);
    setEditNotes(order.notes ?? '');
    setEditShopId(order.shopId ? String(order.shopId) : '');
    setEditAgentId(String(order.agentId));
    setEditPaymentType((order.paymentType as any) ?? 'CASH');
    setEditDueDate(order.dueDate ?? '');
    setEditCategory('');
    setCategoryProducts([]);
    setModalMode('edit');
  };

  const handleEditSave = async () => {
    if (!activeOrder) return;
    try {
      await ordersAPI.update(activeOrder.id, {
        items: editItems.length > 0 ? editItems : undefined,
        notes: editNotes || undefined,
        shopId: editShopId ? Number(editShopId) : null,
        agentId: editAgentId ? Number(editAgentId) : undefined,
        paymentType: editPaymentType,
        dueDate: editPaymentType === 'CREDIT' ? (editDueDate || null) : null,
      });
      setModalMode(null);
      fetchOrders();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при сохранении');
    }
  };

  const addEditItem = (productId: number) => {
    const existing = editItems.find((i) => i.productId === productId);
    if (existing) {
      setEditItems(editItems.map((i) => i.productId === productId ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setEditItems([...editItems, { productId, quantity: 1 }]);
    }
  };

  const updateEditItemQty = (productId: number, qty: number) => {
    if (qty <= 0) {
      setEditItems(editItems.filter((i) => i.productId !== productId));
    } else {
      setEditItems(editItems.map((i) => i.productId === productId ? { ...i, quantity: qty } : i));
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (order: Order) => {
    if (!window.confirm(`Удалить заказ #${order.id}?\n\nТовары вернутся на склад. Действие необратимо!`)) return;
    try {
      await ordersAPI.delete(order.id);
      fetchOrders();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при удалении');
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  if (!isAdmin()) {
    return <div style={{ padding: 24 }}>Доступ запрещён. Только для администраторов.</div>;
  }

  return (
    <>
    <div className="orders-management">
      <div className="page-header">
        <h1>Управление заказами</h1>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Статус</label>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}>
            <option value="">Все статусы</option>
            {(Object.keys(STATUS_LABELS) as OrderStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Агент</label>
          <select value={agentFilter} onChange={(e) => { setAgentFilter(e.target.value); setPage(1); }}>
            <option value="">Все агенты</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.firstName} {u.lastName || ''} ({u.phoneNumber})</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Магазин</label>
          <select value={shopFilter} onChange={(e) => { setShopFilter(e.target.value); setPage(1); }}>
            <option value="">Все магазины</option>
            {shops.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table & Mobile Cards */}
      <div className="table-container orders-mgmt-desktop-table">
        <div className="pagination-controls">
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn-pagination">← Назад</button>
          <span>Страница {page} из {meta.totalPages || 1} (всего: {meta.total})</span>
          <button onClick={() => setPage(page + 1)} disabled={page >= (meta.totalPages || 1)} className="btn-pagination">Вперёд →</button>
        </div>

        {loading ? <div style={{ padding: 24, textAlign: 'center' }}>Загрузка...</div> : (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Агент</th>
                <th>Магазин</th>
                <th>Статус</th>
                <th>Оплата</th>
                <th>Сумма</th>
                <th>Срок долга</th>
                <th>Товары</th>
                <th>Дата</th>
                <th className="col-actions">Действия</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>
                    {order.agent?.firstName} {order.agent?.lastName || ''}
                    <br /><small>{order.agent?.phoneNumber}</small>
                  </td>
                  <td>
                    {order.shop ? (
                      <div>
                        <div style={{ fontWeight: 500 }}>{order.shop.name}</div>
                        <small style={{ fontWeight: 600, color:
                          Number(order.shop.creditLimit) > 0 && Number(order.shop.debt) >= Number(order.shop.creditLimit) ? '#dc2626' :
                          Number(order.shop.debt) > 0 ? '#2563eb' : '#9ca3af'
                        }}>
                          {Number(order.shop.debt) > 0 ? `Долг: ${Number(order.shop.debt).toLocaleString('ru-RU')} с` : 'Без долга'}
                          {Number(order.shop.advance) > 0 && ` / Аванс: +${Number(order.shop.advance).toLocaleString('ru-RU')} с`}
                        </small>
                      </div>
                    ) : <span style={{ color: '#9ca3af' }}>—</span>}
                  </td>
                  <td>
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
                      {STATUS_LABELS[order.status]}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {order.paymentType === 'CASH' && <span style={{ color: '#374151' }}>💵 Наличные</span>}
                    {order.paymentType === 'CREDIT' && <span style={{ color: '#1e40af', fontWeight: 600 }}>📋 В долг</span>}
                    {order.paymentType === 'ADVANCE' && <span style={{ color: '#065f46', fontWeight: 600 }}>💚 С аванса</span>}
                  </td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {Number(order.totalAmount).toLocaleString('ru-RU')} с
                  </td>
                  <td>{(() => {
                    if (!order.dueDate) return <span style={{ color: '#9ca3af' }}>—</span>;
                    const today = new Date().toISOString().split('T')[0];
                    const over = order.dueDate < today && order.status !== 'DELIVERED' && order.status !== 'CANCELLED';
                    return (
                      <span style={{ color: over ? '#ef4444' : '#374151', fontWeight: over ? 700 : 400, whiteSpace: 'nowrap' }}>
                        {over && '⚠️ '}{new Date(order.dueDate).toLocaleDateString('ru-RU')}
                      </span>
                    );
                  })()}</td>
                  <td>
                    {order.items && order.items.length > 0 ? (
                      <button className="btn-items-link" onClick={() => setItemsModal({ orderId: order.id, items: order.items! })}>
                        {order.items.length} поз.
                      </button>
                    ) : '—'}
                  </td>
                  <td>
                    {new Date(order.createdAt).toLocaleDateString('ru-RU')}
                    {order.rejectionReason && (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626', fontWeight: 500 }}>
                        ❌ {order.rejectionReason}
                      </div>
                    )}
                  </td>
                  <td className="col-actions">
                    <div className="order-actions">
                      <button onClick={() => openStatusModal(order)} className="btn-edit"
                        disabled={order.status === 'DELIVERED' || order.status === 'REJECTED'}>
                        Статус
                      </button>
                      <button onClick={() => openEditModal(order)} className="btn-edit"
                        disabled={order.status === 'DELIVERED' || order.status === 'REJECTED'}>
                        Изменить
                      </button>
                      {order.status !== 'REJECTED' && order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
                        <button onClick={() => openRejectModal(order)} className="btn-reject">
                          Отклонить
                        </button>
                      )}
                      <button onClick={async () => { const o = await ordersAPI.getOne(order.id); printInvoice(o); }} className="btn-invoice">
                        🖨 Счёт
                      </button>
                      <button onClick={() => handleDelete(order)} className="btn-delete">
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile Orders Management List */}
      <div className="orders-mgmt-mobile-list">
        <div className="pagination-controls">
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn-pagination">← Назад</button>
          <span>Страница {page} из {meta.totalPages || 1} (всего: {meta.total})</span>
          <button onClick={() => setPage(page + 1)} disabled={page >= (meta.totalPages || 1)} className="btn-pagination">Вперёд →</button>
        </div>

        {loading ? (
          <div className="mobile-empty-card">Загрузка...</div>
        ) : orders.length === 0 ? (
          <div className="mobile-empty-card">Заказов не найдено</div>
        ) : (
          orders.map((order) => {
            const isOverdue = (() => {
              if (!order.dueDate) return false;
              const today = new Date().toISOString().split('T')[0];
              return order.dueDate < today && order.status !== 'DELIVERED' && order.status !== 'CANCELLED';
            })();

            return (
              <div key={order.id} className="mobile-order-card">
                <div className="mobile-card-head">
                  <div className="mobile-card-head-left">
                    <span className="mobile-order-id">#{order.id}</span>
                    <span className="mobile-shop-name">{order.shop?.name || '—'}</span>
                  </div>
                  <span className="status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
                    {STATUS_LABELS[order.status]}
                  </span>
                </div>

                {order.rejectionReason && (
                  <div className="mobile-reject-reason">
                    ❌ {order.rejectionReason}
                  </div>
                )}

                <div className="mobile-card-details">
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Агент:</span>
                    <span className="mobile-detail-val">
                      {order.agent?.firstName} {order.agent?.lastName || ''} ({order.agent?.phoneNumber})
                    </span>
                  </div>

                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Оплата:</span>
                    <span className="mobile-detail-val">
                      {order.paymentType === 'CASH' && '💵 Наличные'}
                      {order.paymentType === 'CREDIT' && '📋 В долг'}
                      {order.paymentType === 'ADVANCE' && '💚 С аванса'}
                    </span>
                  </div>

                  {order.dueDate && (
                    <div className="mobile-detail-row">
                      <span className="mobile-detail-label">Срок оплаты:</span>
                      <span className="mobile-detail-val" style={{ color: isOverdue ? '#ef4444' : '#334155', fontWeight: isOverdue ? 700 : 400 }}>
                        {isOverdue && '⚠️ '}{new Date(order.dueDate).toLocaleDateString('ru-RU')}
                      </span>
                    </div>
                  )}

                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Товары:</span>
                    <span className="mobile-detail-val">
                      {order.items && order.items.length > 0 ? (
                        <button className="btn-items-link" onClick={() => setItemsModal({ orderId: order.id, items: order.items! })}>
                          🔍 {order.items.length} поз. (открыть)
                        </button>
                      ) : '—'}
                    </span>
                  </div>

                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Дата:</span>
                    <span className="mobile-detail-val">{new Date(order.createdAt).toLocaleDateString('ru-RU')}</span>
                  </div>
                </div>

                <div className="mobile-card-summary">
                  <span className="mobile-amount-label">Сумма:</span>
                  <span className="mobile-amount-val">{Number(order.totalAmount).toLocaleString('ru-RU')} с</span>
                </div>

                <div className="mobile-card-actions-grid">
                  <button onClick={() => openStatusModal(order)} className="btn-edit"
                    disabled={order.status === 'DELIVERED' || order.status === 'REJECTED'}>
                    Статус
                  </button>
                  <button onClick={() => openEditModal(order)} className="btn-edit"
                    disabled={order.status === 'DELIVERED' || order.status === 'REJECTED'}>
                    Изменить
                  </button>
                  {order.status !== 'REJECTED' && order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
                    <button onClick={() => openRejectModal(order)} className="btn-reject">
                      Отклонить
                    </button>
                  )}
                  <button onClick={async () => { const o = await ordersAPI.getOne(order.id); printInvoice(o); }} className="btn-invoice">
                    🖨 Счёт
                  </button>
                  <button onClick={() => handleDelete(order)} className="btn-delete">
                    Удалить
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Status modal ── */}
      {modalMode === 'status' && activeOrder && (
        <div className="modal-overlay" onClick={() => setModalMode(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Статус заказа #{activeOrder.id}</h2>
            <div className="form-group">
              <label>Новый статус</label>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as OrderStatus)}>
                {(Object.keys(STATUS_LABELS) as OrderStatus[]).map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Примечание</label>
              <textarea value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} rows={3} placeholder="Комментарий к смене статуса..." />
            </div>
            <div className="modal-actions">
              <button onClick={handleStatusUpdate} className="btn-primary">Сохранить</button>
              <button onClick={() => setModalMode(null)} className="btn-secondary">Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit modal ── */}
      {modalMode === 'edit' && activeOrder && (
        <div className="modal-overlay" onClick={() => setModalMode(null)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Редактировать заказ #{activeOrder.id}</h2>

            <div className="edit-grid">
              {/* Left: meta */}
              <div>
                <div className="form-group">
                  <label>Магазин</label>
                  <select value={editShopId} onChange={(e) => setEditShopId(e.target.value)}>
                    <option value="">— Без магазина —</option>
                    {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Агент</label>
                  <select value={editAgentId} onChange={(e) => setEditAgentId(e.target.value)}>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName || ''} ({u.phoneNumber})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Тип оплаты</label>
                  <select value={editPaymentType} onChange={(e) => setEditPaymentType(e.target.value as any)}>
                    <option value="CASH">💵 Оплата на месте</option>
                    <option value="CREDIT">📋 В долг</option>
                    <option value="ADVANCE">💚 С аванса</option>
                  </select>
                </div>
                <div className="form-group" style={{ display: editPaymentType === 'CREDIT' ? 'block' : 'none' }}>
                  <label>Дата погашения долга</label>
                  <input type="date" value={editDueDate}
                    max={(() => {
                      const shop = shops.find((s) => String(s.id) === editShopId);
                      if (!shop || Number(shop.maxDebtDays) === 0) return undefined;
                      const d = new Date();
                      d.setDate(d.getDate() + Number(shop.maxDebtDays));
                      return d.toISOString().split('T')[0];
                    })()}
                    onChange={(e) => setEditDueDate(e.target.value)} />
                  {editDueDate && (
                    <button type="button" style={{ fontSize: 12, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4 }}
                      onClick={() => setEditDueDate('')}>
                      ✕ Убрать срок
                    </button>
                  )}
                </div>
                <div className="form-group">
                  <label>Примечание</label>
                  <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} />
                </div>
              </div>

              {/* Right: items */}
              <div>
                <div className="form-group">
                  <label>Категория для добавления товаров</label>
                  <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                    <option value="">— Выберите —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                {categoryProducts.length > 0 && (
                  <div className="edit-products-list">
                    {categoryProducts.map((p) => {
                      const item = editItems.find((i) => i.productId === p.id);
                      return (
                        <div key={p.id} className="edit-product-row">
                          <span>
                            <span style={{ fontWeight: 500 }}>{p.name}</span>
                            <small style={{ color: '#6b7280', marginLeft: 4 }}>({p.stock?.quantity ?? 0} на складе)</small>
                            <ProductAttrs attributes={p.attributes} />
                          </span>
                          {item ? (
                            <div className="quantity-controls">
                              <button type="button" onClick={() => updateEditItemQty(p.id, item.quantity - 1)} className="btn-quantity">-</button>
                              <input
                                type="number" value={item.quantity}
                                min={p.unit === 'PIECE' ? '1' : '0.01'}
                                step={p.unit === 'PIECE' ? '1' : '0.01'}
                                className="quantity-input"
                                onChange={(e) => {
                                  const val = p.unit === 'PIECE'
                                    ? parseInt(e.target.value) || 0
                                    : parseFloat(e.target.value) || 0;
                                  updateEditItemQty(p.id, val);
                                }}
                              />
                              <button type="button" onClick={() => updateEditItemQty(p.id, item.quantity + 1)} className="btn-quantity">+</button>
                            </div>
                          ) : (
                            <button type="button" onClick={() => addEditItem(p.id)} className="btn-add">+ Добавить</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="edit-items-summary">
                  <strong>Текущий состав заказа:</strong>
                  {editItems.length === 0
                    ? <p style={{ color: '#9ca3af' }}>Нет позиций</p>
                    : editItems.map((item) => {
                        const allProducts = [...categoryProducts];
                        const p = allProducts.find((x) => x.id === item.productId)
                          || activeOrder.items?.find((i) => i.productId === item.productId)?.product;
                        return (
                          <div key={item.productId} className="summary-item">
                            <span>{p?.name || `Товар #${item.productId}`}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span>{item.quantity} {p?.unit === 'PIECE' ? 'шт.' : p?.unit === 'METER' ? 'м.' : 'кг.'}</span>
                              <button type="button" onClick={() => updateEditItemQty(item.productId, 0)} className="btn-delete" style={{ padding: '2px 8px' }}>×</button>
                            </div>
                          </div>
                        );
                      })
                  }
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={handleEditSave} className="btn-primary" disabled={editItems.length === 0}>Сохранить</button>
              <button onClick={() => setModalMode(null)} className="btn-secondary">Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject modal ── */}
      {modalMode === 'reject' && activeOrder && (
        <div className="modal-overlay" onClick={() => setModalMode(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: '#dc2626' }}>❌ Отклонить заказ #{activeOrder.id}</h2>
            <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 16px' }}>
              Магазин: <strong>{activeOrder.shop?.name || '—'}</strong> · Сумма: <strong>{Number(activeOrder.totalAmount).toLocaleString('ru-RU')} сом</strong>
            </p>
            <div className="form-group">
              <label>Причина отказа <span style={{ color: '#dc2626' }}>*</span></label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="Укажите причину отклонения заказа..."
                autoFocus
                style={{ borderColor: !rejectionReason.trim() ? '#fca5a5' : undefined }}
              />
            </div>
            <div className="modal-actions">
              <button onClick={handleReject} className="btn-reject-confirm" disabled={!rejectionReason.trim()}>
                Отклонить заказ
              </button>
              <button onClick={() => setModalMode(null)} className="btn-secondary">Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>

    {itemsModal && (
      <OrderItemsModal
        orderId={itemsModal.orderId}
        items={itemsModal.items}
        onClose={() => setItemsModal(null)}
      />
    )}
    </>
  );
};

export default OrdersManagement;
